import React, { useState, useRef, useEffect } from 'react';
import './MusicPlayer.css';
import playIcon from '../img/play.png';
import pauseIcon from '../img/pause.png';
import skipIcon from '../img/skip.png';
import plusIcon from '../img/plus.png';
import spotifyIcon from '../img/spotify.png';
import appleIcon from '../img/apple.png';
import linkIcon from '../img/link.png';

interface Track {
  name: string;
  file: string;
  collaborators: string;
}

const tracks: Track[] = [
  { name: 'la paix', file: '/audio/la paix.mp3', collaborators: 'adhiv dhar, chloe catara' },
  { name: 'your orbit', file: '/audio/your orbit.mp3', collaborators: 'adhiv dhar, third culture' },
  { name: 'things change', file: '/audio/things change.mp3', collaborators: 'adhiv dhar, nellybombs' },
  { name: 'everytime', file: '/audio/everytime.mp3', collaborators: 'adhiv dhar'},
  { name: 'need ya', file: '/audio/need ya.mp3', collaborators: 'adhiv dhar, third culture' },
  { name: 'simmering', file: '/audio/simmering.mp3', collaborators: 'adhiv dhar, chloe catara' },
];

// Map track names to their URL routes
const trackRoutes: { [key: string]: string } = {
  'la paix': 'lapaix',
  'your orbit': 'yourorbit',
  'things change': 'thingschange',
  'everytime': 'everytime',
  'need ya': 'needya',
  'simmering': 'simmering'
};

interface MusicPlayerProps {
  currentTrackIndex: number;
  isPlaying: boolean;
  audioRef: React.RefObject<HTMLAudioElement | null>;
  onPlayPause: () => void;
  onNext: () => void;
  onPrev: () => void;
  onTrackChange: (index: number) => void;
}

const MusicPlayer: React.FC<MusicPlayerProps> = ({
  currentTrackIndex,
  isPlaying,
  audioRef,
  onPlayPause,
  onNext,
  onPrev,
  onTrackChange
}) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [vinylRotation, setVinylRotation] = useState(0);
  const [isVinylSpinning, setIsVinylSpinning] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showIntro, setShowIntro] = useState(true); // Always show intro for testing
  const [fadeOut, setFadeOut] = useState(false);
  const animationRef = useRef<number | null>(null);

  const currentTrack = tracks[currentTrackIndex];

  const handleIntroClick = () => {
    setFadeOut(true);
    // Wait for fade out, then hide intro and auto-play
    setTimeout(() => {
      setShowIntro(false);
      setFadeOut(false); // Reset fadeOut so main player can show
      if (!isPlaying) {
        onPlayPause(); // Auto-play
      }
    }, 500);
  };

  // Sync vinyl spinning with global isPlaying state
  useEffect(() => {
    setIsVinylSpinning(isPlaying);
  }, [isPlaying]);

  // Initialize progress bar state from audio element on mount
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Set initial values if audio is already loaded
    if (audio.duration && !isNaN(audio.duration)) {
      setDuration(audio.duration);
    }
    if (audio.currentTime) {
      setCurrentTime(audio.currentTime);
    }
  }, [audioRef]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => onNext();

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioRef, onNext]);


  useEffect(() => {
    if (isVinylSpinning) {
      const animate = () => {
        setVinylRotation(prev => prev + 0.2);
        animationRef.current = requestAnimationFrame(animate);
      };
      animationRef.current = requestAnimationFrame(animate);
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isVinylSpinning]);


  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const newTime = (clickX / width) * duration;

    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleCopyLink = () => {
    const route = trackRoutes[currentTrack.name] || '';
    const url = `https://inorbit.fm/${route}`;
    navigator.clipboard.writeText(url);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setLinkCopied(false);
  };

  if (showIntro) {
    return (
      <div className={`music-player-intro ${fadeOut ? 'fade-out' : ''}`} onClick={handleIntroClick}>
        <div className="spinning-album">
          <div className="album-3d">
            <div className="album-face album-front">
              <img src="/img/cover-medium.jpg" alt="Album Cover" />
            </div>
            <div className="album-face album-back">
              <img src="/img/cover-medium.jpg" alt="Album Cover" />
            </div>
            <div className="album-edge album-edge-top"></div>
            <div className="album-edge album-edge-bottom"></div>
            <div className="album-edge album-edge-left"></div>
            <div className="album-edge album-edge-right"></div>
          </div>
        </div>
        <div className="insert-text">tap to begin</div>
      </div>
    );
  }

  return (
    <div className={`music-player ${showIntro === false && !fadeOut ? 'show-player' : ''}`}>
      <div className="album-cover fade-in-element" style={{ animationDelay: '0.1s' }}>
        <img src="/img/cover-medium.jpg" alt="Album Cover" />
      </div>

      <div className="song-info-container fade-in-element" style={{ animationDelay: '0.25s' }}>
        <div className="song-info-text">
          <div className="song-title">{currentTrack.name}</div>
          <div className="collaborators">{currentTrack.collaborators}</div>
        </div>
        <button className="plus-btn" onClick={() => setShowModal(true)}>
          <img src={plusIcon} alt="Add to playlist" />
        </button>
      </div>

      <div className="progress-section fade-in-element" style={{ animationDelay: '0.4s' }}>
        <div className="progress-times">
          <span className="time-elapsed">{formatTime(currentTime)}</span>
          <span className="time-remaining">{formatTime(duration)}</span>
        </div>
        <div className="music-progress-bar" onClick={handleProgressClick}>
          <div
            className="music-progress-fill"
            style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
          />
        </div>
        <div className="controls">
          <button onClick={onPrev} className="control-btn prev-btn">
            <img src={skipIcon} alt="Previous" />
          </button>
          <button onClick={onPlayPause} className="control-btn play-btn">
            <img src={isPlaying ? pauseIcon : playIcon} alt={isPlaying ? 'Pause' : 'Play'} />
          </button>
          <button onClick={onNext} className="control-btn next-btn">
            <img src={skipIcon} alt="Next" />
          </button>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={handleCloseModal}>×</button>
            <div className="modal-song-info">
              <div className="song-title">{currentTrack.name}</div>
              <div className="collaborators">{currentTrack.collaborators}</div>
            </div>
            <div className="modal-options">
              <a href="https://open.spotify.com/artist/3su9U8scNTBbNeoQVBfxpI?si=fIH1ZE8mT5qztL0dMtxA-A" target="_blank" rel="noopener noreferrer" className="modal-option">
                <img src={spotifyIcon} alt="Spotify" />
                <span>Spotify</span>
              </a>
              <a href="https://music.apple.com/us/artist/adhiv-dhar/1627526294" target="_blank" rel="noopener noreferrer" className="modal-option">
                <img src={appleIcon} alt="Apple Music" />
                <span>Apple Music</span>
              </a>
              <button className="modal-option" onClick={handleCopyLink}>
                <img src={linkIcon} alt="Copy link" />
                <span>{linkCopied ? 'link copied' : 'Copy link'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MusicPlayer;